import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DataTable from 'react-data-table-component';
import { getBlockedCustomers, unblockCustomer } from '../api/services';
import { formatDate } from '../utils/formatDate';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

/* ── DataTable styles ──────────────────────────────────────── */
const dtStyles = {
  headRow:   { style: { backgroundColor:'var(--gray-4)', borderBottom:'1px solid var(--gray-3)', minHeight:'42px' } },
  headCells: { style: { fontSize:'11.5px', fontWeight:'700', color:'var(--gray-1)', textTransform:'uppercase', letterSpacing:'0.6px', paddingLeft:'16px', paddingRight:'12px' } },
  rows:      { style: { fontSize:'13.5px', color:'var(--dark)', borderBottom:'1px solid var(--gray-4)', minHeight:'56px', backgroundColor:'#fff8f8', '&:hover':{ backgroundColor:'#fef2f2' } } },
  cells:     { style: { paddingLeft:'16px', paddingRight:'12px' } },
  pagination:{ style: { borderTop:'1px solid var(--gray-3)', fontSize:'13px', color:'var(--gray-1)', minHeight:'52px' }, pageButtonsStyle:{ borderRadius:'6px', color:'var(--dark)', fill:'var(--gray-1)', '&:hover:not(:disabled)':{ backgroundColor:'var(--gray-4)' } } },
  noData:    { style: { padding:'48px', color:'var(--gray-1)', fontSize:'14px' } },
  progress:  { style: { padding:'48px' } },
};

/**
 * BlockedCustomers Page Component.
 * Table view listing all blocked customers with search filtering and unblock action.
 *
 * @component
 * @returns {JSX.Element} BlockedCustomers page view.
 */
export default function BlockedCustomers() {
  const navigate  = useNavigate();
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');

  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBlockedCustomers();
      setData(res.data.data || []);
    } catch {
      toast.error('Failed to load blocked customers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    load();
  }, [load]);

  const handleUnblock = async (id, name) => {
    const result = await Swal.fire({
      title: 'Unblock Customer?',
      html: `<b>${name}</b> will be allowed to make transactions again.`,
      icon: 'question', showCancelButton: true,
      confirmButtonText: '✅ Yes, Unblock', cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a', cancelButtonColor: '#6b7280',
    });
    if (!result.isConfirmed) return;
    try {
      await unblockCustomer(id);
      toast.success(`${name} has been unblocked.`);
      load();
    } catch {
      toast.error('Failed to unblock customer.');
    }
  };

  /* ── Filter by search ───────────────────────────── */
  const filtered = search.trim()
    ? data.filter(c => {
        const q = search.toLowerCase();
        return (
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
          c.identifications?.[0]?.idNumber?.toLowerCase().includes(q) ||
          c.contacts?.[0]?.value?.toLowerCase().includes(q) ||
          c.blockedReason?.toLowerCase().includes(q)
        );
      })
    : data;

  /* ── Columns ────────────────────────────────────── */
  const columns = [
    {
      name: 'CUSTOMER',
      selector: r => `${r.firstName} ${r.lastName}`,
      sortable: true, minWidth: '180px',
      cell: r => (
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:8, background:'#fecaca', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, flexShrink:0, color:'#dc2626' }}>
            {r.firstName?.[0]}{r.lastName?.[0]}
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:13.5 }}>{r.firstName} {r.middleName ? r.middleName[0]+'. ' : ''}{r.lastName}</div>
            <div style={{ fontSize:11, color:'var(--gray-1)', fontFamily:'monospace' }}>#{r._id.slice(-6).toUpperCase()}</div>
          </div>
        </div>
      ),
    },
    {
      name: 'ID',
      minWidth: '150px',
      cell: r => r.identifications?.[0] ? (
        <div>
          <div style={{ fontSize:12.5, fontWeight:600 }}>{r.identifications[0].type}</div>
          <div style={{ fontSize:11.5, fontFamily:'monospace', color:'var(--gray-1)' }}>{r.identifications[0].idNumber}</div>
        </div>
      ) : <span className="text-muted">—</span>,
    },
    {
      name: 'CONTACT',
      minWidth: '140px',
      cell: r => r.contacts?.[0] ? (
        <div>
          <div style={{ fontSize:13 }}>{r.contacts[0].value}</div>
          <div style={{ fontSize:11, color:'var(--gray-1)' }}>{r.contacts[0].type}</div>
        </div>
      ) : <span className="text-muted">—</span>,
    },
    {
      name: 'BLOCKED REASON',
      selector: r => r.blockedReason,
      sortable: true, minWidth: '180px',
      cell: r => <span style={{ color:'#dc2626', fontWeight:600, fontSize:13 }}>{r.blockedReason || 'Fraud / Suspicious activity'}</span>,
    },
    {
      name: 'BLOCKED ON',
      selector: r => r.blockedAt,
      sortable: true, minWidth: '120px',
      cell: r => <span className="td-date">{r.blockedAt ? formatDate(r.blockedAt) : '—'}</span>,
    },
    {
      name: 'BLOCKED BY',
      selector: r => r.blockedBy?.name,
      sortable: true, minWidth: '120px',
      cell: r => <span style={{ fontSize:12 }}>{r.blockedBy?.name || '—'}</span>,
    },
    {
      name: 'ACTIONS',
      minWidth: '140px',
      ignoreRowClick: true,
      cell: r => (
        <div style={{ display:'flex', gap:6 }}>
          <button className="btn btn-sm btn-outline" onClick={() => navigate(`/customers/${r._id}`)}>View</button>
          <button className="btn btn-sm btn-ghost" style={{ color:'var(--success)', fontWeight:700 }}
            onClick={() => handleUnblock(r._id, `${r.firstName} ${r.lastName}`)}>✅ Unblock</button>
        </div>
      ),
    },
  ];

  return (
    <Layout title="Blocked Customers">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:22 }}>🚫 Blocked Customers</h2>
          <p className="text-muted text-sm mt-1">
            {loading ? 'Loading…' : `${data.length} blocked customer${data.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={load} className="btn btn-outline btn-sm">🔄 Refresh</button>
          <button onClick={() => navigate('/customers')} className="btn btn-ghost btn-sm">← All Customers</button>
        </div>
      </div>

      {!loading && data.length > 0 && (
        <div style={{ background:'#fef2f2', border:'1.5px solid #fca5a5', borderRadius:8, padding:'12px 16px', marginBottom:16 }}>
          <div style={{ fontWeight:700, color:'#dc2626', fontSize:13 }}>
            ⚠️ These customers are flagged. Transactions from blocked customers will be automatically rejected.
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card mb-4" style={{ padding:'12px 16px' }}>
        <div style={{ display:'flex', gap:8 }}>
          <input className="form-control" placeholder="Search by name, ID, phone, reason…"
            value={search} onChange={e => setSearch(e.target.value)} style={{ flex:1 }} />
          {search && <button className="btn btn-outline btn-sm" onClick={() => setSearch('')}>✕</button>}
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={filtered}
          progressPending={loading}
          progressComponent={<div className="table-loading-cell"><div className="spinner spinner-dark spinner-centered"/></div>}
          noDataComponent={
            <div className="table-empty-cell">
              <div className="empty-icon">✅</div>
              <p>{search ? `No results for "${search}"` : 'No blocked customers. All customers are in good standing.'}</p>
            </div>
          }
          pagination
          paginationRowsPerPageOptions={[10, 25, 50]}
          highlightOnHover
          defaultSortFieldId={5}
          defaultSortAsc={false}
          customStyles={dtStyles}
        />
      </div>
    </Layout>
  );
}
